/* eslint-disable prettier/prettier */
/* eslint-disable react/require-default-props */
/* eslint-disable no-console */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "@shared/dayjs";
import PropTypes from "prop-types";

import ErrorBanner from "@shared/v2/ErrorBanner";
import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button/Button";
import Checkbox from "@shared/v2/Checkbox";
import DatePicker from "@shared/v2/DatePicker";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import TextInput from "@shared/v2/TextInput";
import Tooltip from "@shared/v2/Tooltip";
import CircleInfoSolidV6 from "@shared/v2/Icomoon/Icons/CircleInfoSolidV6";
import { PlusSolidV6 } from "@shared/v2/Icomoon";
import MlsListingDropdown from "@shared/MlsListingDropdown";
import useMlsListingDetails from "@shared/MlsListingDropdown/useMlsListingDetails";
import TextButton from "./TextButton";
import ListingPreviewCard from "./ListingPreviewCard";
import RoleForTransaction from "./RoleForTransaction";
import { TRANSACTION_STATUS } from "./ListingTransactionModal";
import AddEditModalBody from "../shared/AddressModal/AddEditModalBody";
import format from "../shared/formatters";
import ConnectServicesClientSelector from "../TransactionDetailsPage/shared/ConnectServicesClientSelector";
import apiClient from "../TransactionDetailsPage/Main/Details/api/apiClient";
import { useAccountProducts } from "../reducers/layoutReducer/selectors";
import { useSearchPeople } from "../QuickSearch/components/SearchPeople";

const useAccountPeopleSearchClient = () => {
  const [search, setSearch] = useState("");
  const { data, loading } = useSearchPeople({ scope: "account", search, excludeTrashAndArchived: true });
  const options = useMemo(() => data.map((p) => ({ value: p.object_id, label: p.label })), [data]);
  const noOptionsMessage =
    search.length > 2
      ? "No client found"
      : `Enter ${Math.max(3 - search.length, 0)} more characters to search`;
  return {
    options,
    setSearch,
    isLoading: loading,
    error: null,
    noOptionsMessage,
  };
};

const ROLE_TITLE_MAPPING = {
  BuyerListing: "Buyer",
  SellerListing: "Seller",
  TenantListing: "Renter",
  LandlordListing: "Landlord",
};

const SECTION_HEADER_CLASS = "tw-text-gray-base tw-text-18d tw-font-bold tw-mb-[-8px]";

const SyncWithAddressLabel = () => (
  <span className="tw-flex tw-items-center tw-gap-[4px]">
    Sync with Address
    <Tooltip
      trigger={
        <span className="tw-flex tw-items-center">
          <CircleInfoSolidV6 size="custom" width={12} height={12} className="tw-text-neutral-gray-50" />
        </span>
      }
      content="When enabled, the transaction title will automatically sync with the property address."
      placement="top"
    />
  </span>
);

const ListingDetailsImportMode = ({
  onSelectBlossorId,
  onClearListing,
  listingData,
  onToggleMode,
  isRequired,
}) => (
  <div className="tw-flex tw-flex-col tw-gap-[16px]">
    <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
      <FieldLabel label="Import Listing Details" isRequired={isRequired} />
      <TextButton size="small" onClick={onToggleMode}>
        ENTER DETAILS
      </TextButton>
    </div>
    {listingData ? (
      <ListingPreviewCard
        containerClassName="tw-w-full"
        listingData={listingData}
        clearListing={onClearListing}
      />
    ) : (
      <MlsListingDropdown
        onChange={(option) => onSelectBlossorId(option?.value || null)}
        placeholder="Search for a listing address to auto-populate the information below"
      />
    )}
  </div>
);

ListingDetailsImportMode.propTypes = {
  onSelectBlossorId: PropTypes.func,
  onClearListing: PropTypes.func,
  listingData: PropTypes.shape({}),
  onToggleMode: PropTypes.func,
  isRequired: PropTypes.bool,
};

ListingDetailsImportMode.defaultProps = {
  onSelectBlossorId: () => {},
  onClearListing: () => {},
  listingData: null,
  onToggleMode: () => {},
  isRequired: false,
};

const ListingDetailsEnterForm = ({
  formData,
  updateField,
  propertyTypes,
  setFocusedPriceField,
  handleCurrencyChange,
  getCurrencyDisplayValue,
  addressError,
  setAddressError,
  updateCompleteAddress,
  isAddressRequired,
}) => {
  const propertyTypeValue = propertyTypes.find((o) => o.value === formData.propertyType);

  return (
    <div className="tw-flex tw-flex-col tw-gap-[16px]">
      <div className="tw-flex tw-gap-[16px]">
        <Dropdown
          containerClassName="tw-flex-1"
          label="Property Type"
          options={propertyTypes}
          onChange={(option) => updateField("propertyType", option?.value)}
          value={propertyTypeValue}
          placeholder="Select"
          isSearchable
          isRequired
          menuShouldComeToFront
        />
        <TextInput
          label="MLS #"
          value={formData.mlsNumber}
          onChange={(e) => updateField("mlsNumber", e.target.value)}
          containerClassName="tw-flex-1"
        />
      </div>

      <AddEditModalBody
        hideAddressType
        hideSecondaryAddress
        standardized
        addressType=""
        setAddressType={() => {}}
        streetAddress={formData.streetAddress}
        setStreetAddress={(value) => updateField("streetAddress", value)}
        secondaryAddress={formData.secondaryAddress}
        setSecondaryAddress={(value) => updateField("secondaryAddress", value)}
        city={formData.city}
        setCity={(value) => updateField("city", value)}
        locality={formData.locality}
        setLocality={(value) => updateField("locality", value)}
        postalCode={formData.postalCode}
        setPostalCode={(value) => updateField("postalCode", value)}
        country={formData.country}
        setCountry={(value) => updateField("country", value)}
        error={addressError}
        setError={setAddressError}
        updateCompleteAddress={updateCompleteAddress}
        isStreetAddressRequired={isAddressRequired}
        areAddressFieldsRequired={isAddressRequired}
      />

      <div className="tw-flex tw-gap-[16px]">
        <TextInput
          label="Listing Price"
          placeholder="0"
          value={getCurrencyDisplayValue("price", formData.price)}
          onChange={handleCurrencyChange("price")}
          onFocus={() => setFocusedPriceField("price")}
          onBlur={() => setFocusedPriceField(null)}
          containerClassName="tw-flex-1"
        />
        <TextInput
          label="Estimated Close Price"
          placeholder="0"
          value={getCurrencyDisplayValue("estClosePrice", formData.estClosePrice)}
          onChange={handleCurrencyChange("estClosePrice")}
          onFocus={() => setFocusedPriceField("estClosePrice")}
          onBlur={() => setFocusedPriceField(null)}
          containerClassName="tw-flex-1"
        />
      </div>

      <div className="tw-flex tw-gap-[16px]">
        <TextInput
          label="Beds"
          value={formData.beds}
          onChange={(e) => updateField("beds", e.target.value)}
          containerClassName="tw-flex-1 tw-min-w-0"
        />
        <TextInput
          label="Baths"
          value={formData.baths}
          onChange={(e) => updateField("baths", e.target.value)}
          containerClassName="tw-flex-1 tw-min-w-0"
        />
        <TextInput
          label="SqFt"
          value={formData.sqft}
          onChange={(e) => updateField("sqft", e.target.value)}
          containerClassName="tw-flex-1 tw-min-w-0"
        />
      </div>

      <TextInput
        label="Listing Description"
        value={formData.description}
        onChange={(e) => updateField("description", e.target.value)}
        multiline
      />
    </div>
  );
};

ListingDetailsEnterForm.propTypes = {
  formData: PropTypes.shape({
    propertyType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mlsNumber: PropTypes.string,
    streetAddress: PropTypes.string,
    secondaryAddress: PropTypes.string,
    city: PropTypes.string,
    locality: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
    price: PropTypes.string,
    estClosePrice: PropTypes.string,
    beds: PropTypes.string,
    baths: PropTypes.string,
    sqft: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  propertyTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  setFocusedPriceField: PropTypes.func.isRequired,
  handleCurrencyChange: PropTypes.func.isRequired,
  getCurrencyDisplayValue: PropTypes.func.isRequired,
  addressError: PropTypes.string,
  setAddressError: PropTypes.func.isRequired,
  updateCompleteAddress: PropTypes.func.isRequired,
  isAddressRequired: PropTypes.bool,
};

ListingDetailsEnterForm.defaultProps = {
  propertyTypes: [],
  addressError: null,
  isAddressRequired: false,
};

const optionShape = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const INITIAL_LISTING_FORM_DATA = {
  propertyType: "",
  mlsNumber: "",
  streetAddress: "",
  secondaryAddress: "",
  city: "",
  locality: "",
  postalCode: "",
  country: "US",
  price: "",
  estClosePrice: "",
  beds: "",
  baths: "",
  sqft: "",
  description: "",
};

const AddBuyerTenantTransactionModal = ({
  show,
  onHide,
  sources,
  propertyTypes,
  transactionType,
  personId,
  personName,
  defaultPropertyType,
}) => {
  const accountProducts = useAccountProducts();

  const [transactionTitle, setTransactionTitle] = useState("");
  const [syncWithAddress, setSyncWithAddress] = useState(true);
  const [status, setStatus] = useState(TRANSACTION_STATUS.find((opt) => opt.value === "pipeline"));
  const [source, setSource] = useState(null);
  const [closeDate, setCloseDate] = useState("");
  const [mutualAcceptanceDate, setMutualAcceptanceDate] = useState("");
  const [estimatedClosePrice, setEstimatedClosePrice] = useState("");
  const [selectedBlossorId, setSelectedBlossorId] = useState(null);
  const [listingDetailsMode, setListingDetailsMode] = useState("import");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [personRoleId, setPersonRoleId] = useState(null);
  const [clientRoles, setClientRoles] = useState([]);

  const [connectServicesClient, setConnectServicesClient] = useState(null);
  const [connectServicesDisableReason, setConnectServicesDisableReason] = useState(null);
  const [isConnectServicesValid, setIsConnectServicesValid] = useState(false);

  const [listingFormData, setListingFormData] = useState(INITIAL_LISTING_FORM_DATA);
  const [focusedPriceField, setFocusedPriceField] = useState(null);
  const [addressError, setAddressError] = useState(null);

  const [roles, setRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [agentsOnTransaction, setAgentsOnTransaction] = useState([]);
  const [addRole, setAddRole] = useState(false);
  const [primaryPosition] = useState(0);

  const createdPropertyTypeIdsRef = useRef({});

  useEffect(() => {
    const loadRoles = async () => {
      const path =
        transactionType === "BuyerListing"
          ? "/roles/pending_transaction_roles"
          : "/roles/listing_transaction_roles";

      const response = await axios.get(path, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      let defaultRoles = response.data.default_roles;
      const { roles: loadedRoles } = response.data;

      if (transactionType === "BuyerListing") {
        const buyersAgentRole = loadedRoles.find((role) => role.label === "Buyer's Agent");
        defaultRoles = [
          buyersAgentRole,
          ...defaultRoles.filter((defaultRole) => defaultRole.label !== "Buyer's Agent"),
        ];
      } else {
        const listingAgentRole = loadedRoles.find((role) => role.label === "Listing Agent");
        defaultRoles = [
          listingAgentRole,
          ...defaultRoles.filter((defaultRole) => defaultRole.label !== "Listing Agent"),
        ];
      }

      defaultRoles = defaultRoles.filter((defaultRole) => defaultRole !== undefined);

      setRoles(loadedRoles);
      setAvailableRoles(
        loadedRoles.filter((role) => !defaultRoles.some((defaultRole) => role.value === defaultRole.value)),
      );
      setAgentsOnTransaction(
        defaultRoles.map((role, index) => ({
          ...(role.agents[0].label.includes("Role Default") ? role.agents[0] : null),
          roleId: role.value,
          visible: "false",
          primary: index === 0 ? "true" : "false",
        })),
      );
    };

    loadRoles();
  }, [transactionType]);

  useEffect(() => {
    const loadClientRoles = async () => {
      const response = await axios.get("/api/v4/transactions/allAvailableRoles/people/roles");
      const loadedClientRoles = response.data?.client || [];
      setClientRoles(loadedClientRoles);

      if (personId) {
        const title = ROLE_TITLE_MAPPING[transactionType];
        const roleObj = loadedClientRoles.find((role) => role.title === title);
        setPersonRoleId(roleObj?.id || null);
      }
    };

    loadClientRoles();
  }, [personId, transactionType]);

  const handleVisibilitySelect = (visible, roleId) => {
    setAgentsOnTransaction((prev) =>
      prev.map((agent) => (agent.roleId === roleId ? { ...agent, visible } : agent)),
    );
  };

  const handleAgentSelect = (agentId, roleId) => {
    setAgentsOnTransaction((prev) =>
      prev.map((agent) => (agent.roleId === roleId ? { ...agent, value: agentId } : agent)),
    );
  };

  const onRoleSelect = ({ value }) => {
    let defaultAgent = roles.find((role) => role.value === value).agents[0];
    defaultAgent = defaultAgent.label.includes("Role Default") ? defaultAgent : null;
    const newAgentRole = { ...defaultAgent, visible: "false", roleId: value, primary: "false" };

    if (!agentsOnTransaction.length) {
      newAgentRole.primary = "true";
    }

    setAddRole(false);
    setAvailableRoles((prev) => prev.filter((r) => r.value !== value));
    setAgentsOnTransaction((prev) => [...prev, newAgentRole]);
  };

  const handleRemoveRole = (roleId) => {
    const matchingRole = roles.find((role) => role.value === roleId);
    const matchingAgent = agentsOnTransaction.find((agent) => agent.roleId === roleId);
    const newAgentsOnTransaction = agentsOnTransaction.filter((agent) => agent.roleId !== roleId);

    if (matchingAgent?.primary === "true" && newAgentsOnTransaction.length) {
      newAgentsOnTransaction[0].primary = "true";
    }

    setAvailableRoles((prev) => [...prev, matchingRole]);
    setAgentsOnTransaction(newAgentsOnTransaction);
  };

  const setPrimaryAgent = (roleId) => {
    setAgentsOnTransaction((prev) =>
      prev.map((agent) => ({
        ...agent,
        primary: String(agent.roleId) === String(roleId) ? "true" : "false",
      })),
    );
  };

  const mlsListingDetails = useMlsListingDetails(selectedBlossorId);
  const listingData = mlsListingDetails.data;

  useEffect(() => {
    if (listingData?.price != null) {
      setEstimatedClosePrice(format.currency(listingData.price, 0));
    }
    if (syncWithAddress && listingData?.streetAddress) {
      setTransactionTitle(listingData.streetAddress);
    }
  }, [listingData?.price, listingData?.streetAddress, syncWithAddress]);

  useEffect(() => {
    if (syncWithAddress && listingDetailsMode === "enter" && listingFormData.streetAddress) {
      setTransactionTitle(listingFormData.streetAddress);
    }
  }, [syncWithAddress, listingDetailsMode, listingFormData.streetAddress]);

  const updateListingField = (field, value) => {
    setListingFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCurrencyChange = (field) => (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    if (raw === "" || !Number.isNaN(Number(raw))) {
      updateListingField(field, raw);
    }
  };

  const getCurrencyDisplayValue = (field, value) =>
    focusedPriceField === field ? value : format.currency(Number(value) || 0, 0);

  const updateCompleteAddress = ({ streetAddress, city, locality, postalCode, country }) => {
    setListingFormData((prev) => ({
      ...prev,
      streetAddress: streetAddress || prev.streetAddress,
      city: city || prev.city,
      locality: locality || prev.locality,
      postalCode: postalCode || prev.postalCode,
      country: country || prev.country,
    }));
  };

  const handleSyncWithAddressChange = (checked) => {
    setSyncWithAddress(checked);
    if (checked && listingData?.streetAddress) {
      setTransactionTitle(listingData.streetAddress);
    }
  };

  const handleClearListing = () => {
    setSelectedBlossorId(null);
    mlsListingDetails.actions.clear();
  };

  const toggleListingDetailsMode = () => {
    setSelectedBlossorId(null);
    mlsListingDetails.actions.clear();
    setEstimatedClosePrice("");
    setListingFormData(INITIAL_LISTING_FORM_DATA);
    setAddressError(null);
    setListingDetailsMode((prev) => (prev === "import" ? "enter" : "import"));
  };

  const isEnterMode = listingDetailsMode === "enter";
  const isPending = status?.value === "pending";
  const isPipeline = status?.value === "pipeline";

  const daysUntilClose = useMemo(() => {
    if (!closeDate) return null;
    const parsed = dayjs(closeDate, "MM/DD/YYYY");
    if (!parsed.isValid()) return null;
    return parsed.diff(dayjs().startOf("day"), "days");
  }, [closeDate]);

  const isCloseDateInPast = daysUntilClose !== null && daysUntilClose < 0;

  const showConnectServices =
    isPending &&
    accountProducts.connectService &&
    (transactionType === "BuyerListing" || transactionType === "TenantListing");

  const clientOptions = useMemo(() => {
    if (!personId || !personName || !personRoleId) return [];
    return [
      {
        label: personName,
        value: personId,
        roleId: personRoleId,
        name: personName,
        email: "",
      },
    ];
  }, [personId, personName, personRoleId]);

  useEffect(() => {
    if (isCloseDateInPast) {
      setConnectServicesClient(null);
      setConnectServicesDisableReason(null);
    }
  }, [isCloseDateInPast]);

  const hasRequiredAddressFields =
    isPipeline ||
    (isEnterMode
      ? listingFormData.streetAddress.trim() !== "" &&
        listingFormData.city.trim() !== "" &&
        listingFormData.locality.trim() !== "" &&
        listingFormData.postalCode.trim() !== "" &&
        listingFormData.country.trim() !== ""
      : (listingData?.streetAddress || "").trim() !== "");

  const isFormValid =
    (syncWithAddress || transactionTitle.trim() !== "") &&
    source !== null &&
    (!isPending || closeDate !== "") &&
    hasRequiredAddressFields &&
    (!isEnterMode || listingFormData.propertyType) &&
    (!showConnectServices || isConnectServicesValid);

  const getListingDetails = () => {
    if (isEnterMode) {
      return {
        streetAddress: listingFormData.streetAddress,
        city: listingFormData.city,
        state: listingFormData.locality,
        postalCode: listingFormData.postalCode,
        country: listingFormData.country,
        price: listingFormData.price,
        mlsNumber: listingFormData.mlsNumber,
        beds: listingFormData.beds,
        baths: listingFormData.baths,
        sqFt: listingFormData.sqft,
        description: listingFormData.description,
        photos: [],
        propertyType:
          propertyTypes.find((pt) => pt.value === listingFormData.propertyType) || defaultPropertyType,
        estClosePrice: listingFormData.estClosePrice,
      };
    }

    const listingPropertyType = listingData?.standardPropertyType || listingData?.propertyType;
    const matchedPropertyType =
      propertyTypes.find((pt) => pt.label === listingPropertyType) ||
      (listingPropertyType ? { label: listingPropertyType, value: -1 } : defaultPropertyType);

    return {
      streetAddress: listingData?.streetAddress || "",
      city: listingData?.city || "",
      state: listingData?.state || "",
      postalCode: listingData?.postalCode || "",
      country: listingData?.country || "US",
      price: listingData?.price || "",
      mlsNumber: listingData?.mlsNum || "",
      beds: listingData?.bedrooms || "",
      baths: listingData?.bathsTotalDecimal || "",
      sqFt: listingData?.squareFeet || "",
      description: listingData?.remarks || "",
      photos: listingData?.photos || [],
      propertyType: matchedPropertyType,
      estClosePrice: estimatedClosePrice.replace(/[^0-9.]/g, ""),
    };
  };

  const resolvePropertyTypeId = async (details) => {
    if (details.propertyType?.value !== -1) {
      return details.propertyType?.value || "";
    }

    const { label } = details.propertyType;

    if (createdPropertyTypeIdsRef.current[label]) {
      return createdPropertyTypeIdsRef.current[label];
    }

    const propertyTypeFormData = new FormData();
    propertyTypeFormData.append("utf8", "✓");
    propertyTypeFormData.append("authenticity_token", ReactOnRails.authenticityToken());
    propertyTypeFormData.append("property_type[name]", label);

    const response = await axios.post("/property_types.json", propertyTypeFormData);
    createdPropertyTypeIdsRef.current[label] = response.data.id;
    return response.data.id;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setValidationErrors([]);

    const details = getListingDetails();

    let propertyTypeId;
    try {
      propertyTypeId = await resolvePropertyTypeId(details);
    } catch (error) {
      const html = error.response?.data?.content || "Validation failed";
      setValidationErrors([{ render: html }]);
      setIsSubmitting(false);
      return;
    }

    const title = syncWithAddress ? details.streetAddress : transactionTitle;
    const closePriceField = status?.value === "sold" ? "close_price" : "est_close_price";

    const formData = new FormData();
    formData.append("utf8", "✓");
    formData.append("authenticity_token", ReactOnRails.authenticityToken());

    formData.append("listing[type]", transactionType);
    formData.append("property_type", propertyTypeId);
    formData.append("listing[price]", details.price);
    formData.append("listing[mls_number]", details.mlsNumber);
    formData.append("listing[status]", status?.value);
    formData.append("listing[title]", title);
    formData.append(`listing[${closePriceField}]`, details.estClosePrice);

    const toIsoDate = (value) => {
      if (!value) return "";
      const parsed = dayjs(value, "MM/DD/YYYY", true);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
    };

    const closeDateIso = toIsoDate(closeDate);
    const mutualAcceptanceDateIso = toIsoDate(mutualAcceptanceDate);

    if (closeDateIso) {
      formData.append("listing[close_date]", closeDateIso);
    }
    if (mutualAcceptanceDateIso) {
      formData.append("listing[mutual_acceptance_date]", mutualAcceptanceDateIso);
    }

    formData.append("listing[address_attributes][street_address]", details.streetAddress);
    formData.append("listing[address_attributes][city]", details.city);
    formData.append("listing[address_attributes][locality]", details.state);
    formData.append("listing[address_attributes][postal_code]", details.postalCode);
    formData.append("listing[address_attributes][country]", details.country);
    formData.append("listing[address_attributes][address_type]", "listing");

    if (personId) {
      formData.append(`listing[viewers_attributes][${personId}][person_id]`, personId);
      formData.append(`listing[viewers_attributes][${personId}][role_id]`, personRoleId);
      formData.append(`listing[viewers_attributes][${personId}][visible_to_public]`, true);
      formData.append(`listing[viewers_attributes][${personId}][collaborator]`, false);
      formData.append(`listing[viewers_attributes][${personId}][gets_updates]`, false);
    }

    agentsOnTransaction.forEach((agent, i) => {
      formData.append(`listing[team_members_attributes][${agent.value}${i}][person_id]`, agent.value);
      formData.append(`listing[team_members_attributes][${agent.value}${i}][role_id]`, agent.roleId);
      formData.append(
        `listing[team_members_attributes][${agent.value}${i}][visible_to_public]`,
        agent.visible,
      );
      formData.append(`listing[team_members_attributes][${agent.value}${i}][primary]`, agent.primary);
    });

    if (selectedBlossorId) {
      formData.append("listing[blossor_id]", selectedBlossorId);
      formData.append("listing[use_mls_photos]", true);
    }

    formData.append("add_new_role_id", "");

    formData.append("listing[details_attributes][][name]", "beds");
    formData.append("listing[details_attributes][][value]", details.beds);
    formData.append("listing[details_attributes][][name]", "baths");
    formData.append("listing[details_attributes][][value]", details.baths);
    formData.append("listing[details_attributes][][name]", "sqft");
    formData.append("listing[details_attributes][][value]", details.sqFt);
    formData.append("listing[details_attributes][][_destroy]", false);

    formData.append("listing[source_id]", source?.value || "");
    formData.append("listing[description]", details.description);
    formData.append("listing[published]", false);

    if (transactionType === "BuyerListing") {
      formData.append("listing[only_pending_info]", "true");
    }

    if (showConnectServices) {
      formData.append("return_listing_details", "true");
    }

    try {
      await axios.post("/listings/validate_new_listing_steps", formData);
    } catch (error) {
      const html = error.response?.data?.content || "Validation failed";
      setValidationErrors([{ render: html }]);
      setIsSubmitting(false);
      return;
    }

    try {
      const createResponse = await axios.post("/listings", formData);

      if (!showConnectServices) {
        window.location.replace(createResponse.data);
        return;
      }

      const { uuid: listingUuid, path: listingPath } = createResponse.data || {};

      if (listingUuid) {
        try {
          if (isCloseDateInPast) {
            await apiClient.createConnectedService(listingUuid);
          } else if (connectServicesDisableReason) {
            await apiClient.createConnectedService(listingUuid, {
              disabledReason: connectServicesDisableReason.value,
            });
          } else if (connectServicesClient) {
            const payload =
              connectServicesClient.role?.value && connectServicesClient.client?.value
                ? {
                    roleId: connectServicesClient.role.value,
                    personId: connectServicesClient.client.value,
                  }
                : { roleId: personRoleId, personId };
            await apiClient.createConnectedService(listingUuid, payload);
          }
        } catch {
          console.log("Failed to create connected service, but transaction was created successfully.");
        }
      }

      window.location.replace(listingPath || `/transactions/${listingUuid}/tasks`);
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      className="tw-flex tw-justify-center tw-items-center"
      contentClassName="tw-max-w-[640px] tw-w-[100vw] tw-max-h-[85vh] tw-flex tw-flex-col"
      show={show}
      onHide={onHide}
    >
      <Modal.Header
        titleClassName="tw-text-neutral-gray-50 tw-mb-32px"
        title="Add Transaction Information"
        closeButton
      />
      {validationErrors.length > 0 && (
        <div className="[&_dl]:tw-m-0 tw-mb-[16px]">
          <ErrorBanner errors={validationErrors} />
        </div>
      )}

      <Modal.Body className="tw-flex tw-flex-col tw-gap-[32px] tw-flex-1 tw-overflow-y-auto tw-mb-[32px]">
        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <div className={SECTION_HEADER_CLASS}>Transaction Details</div>

          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <TextInput
              label="Transaction Title"
              value={transactionTitle}
              onChange={(e) => setTransactionTitle(e.target.value)}
              placeholder="Enter Transaction Title"
              isRequired
              disabled={syncWithAddress}
            />
            <Checkbox
              checked={syncWithAddress}
              onChange={(e) => handleSyncWithAddressChange(e.target.checked)}
              label={<SyncWithAddressLabel />}
            />
          </div>

          <div className="tw-flex tw-gap-[16px]">
            <Dropdown
              containerClassName="tw-flex-1"
              label="Transaction Status"
              options={TRANSACTION_STATUS}
              value={status}
              onChange={setStatus}
              menuShouldComeToFront
            />
            <Dropdown
              containerClassName="tw-flex-1"
              label="Source"
              options={sources}
              value={source}
              onChange={setSource}
              placeholder="Select"
              isSearchable
              isRequired
              menuShouldComeToFront
            />
          </div>

          <div className="tw-flex tw-gap-[16px]">
            <div className="tw-flex-1">
              <FieldLabel label="Mutual Acceptance Date" />
              <DatePicker
                value={mutualAcceptanceDate}
                onChange={setMutualAcceptanceDate}
                position="bottom"
                containerClassName="tw-w-full"
                inputProps={{
                  placeholder: "MM/DD/YYYY",
                }}
              />
            </div>
            <div className="tw-flex-1">
              <FieldLabel label="Close Date" isRequired={status?.value === "pending"} />
              <DatePicker
                value={closeDate}
                onChange={setCloseDate}
                position="bottom"
                containerClassName="tw-w-full"
                inputProps={{
                  placeholder: "MM/DD/YYYY",
                }}
              />
            </div>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className={SECTION_HEADER_CLASS}>Listing Details</div>
            {isEnterMode && (
              <TextButton size="small" onClick={toggleListingDetailsMode}>
                IMPORT LISTING
              </TextButton>
            )}
          </div>

          {isEnterMode ? (
            <ListingDetailsEnterForm
              formData={listingFormData}
              updateField={updateListingField}
              propertyTypes={propertyTypes}
              setFocusedPriceField={setFocusedPriceField}
              handleCurrencyChange={handleCurrencyChange}
              getCurrencyDisplayValue={getCurrencyDisplayValue}
              addressError={addressError}
              setAddressError={setAddressError}
              updateCompleteAddress={updateCompleteAddress}
              isAddressRequired={!isPipeline}
            />
          ) : (
            <>
              <ListingDetailsImportMode
                onSelectBlossorId={setSelectedBlossorId}
                onClearListing={handleClearListing}
                listingData={listingData}
                onToggleMode={toggleListingDetailsMode}
                isRequired={!isPipeline}
              />

              <TextInput
                label="Estimated Close Price"
                value={estimatedClosePrice}
                onChange={(e) => setEstimatedClosePrice(e.target.value)}
                containerClassName="tw-w-[calc(50%-8px)]"
                placeholder="$ 0"
              />
            </>
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <div className={SECTION_HEADER_CLASS}>Roles</div>

          {agentsOnTransaction.map((agent, position) => (
            <RoleForTransaction
              key={agent.roleId}
              role={roles.find((role) => role.value === agent.roleId)}
              visible={agent.visible}
              selectedAgentId={agent.value}
              position={position}
              primaryPosition={primaryPosition}
              primary={agent.primary}
              showVisibility={transactionType !== "BuyerListing"}
              setPrimaryAgent={setPrimaryAgent}
              handleAgentSelect={handleAgentSelect}
              handleVisibilitySelect={handleVisibilitySelect}
              handleRemoveRole={handleRemoveRole}
            />
          ))}

          {addRole && (
            <div>
              <Dropdown
                label="Role"
                isSearchable
                options={availableRoles}
                onChange={onRoleSelect}
                menuShouldComeToFront
              />
            </div>
          )}
          <div>
            <Button onClick={() => setAddRole((prev) => !prev)}>
              Add Role <PlusSolidV6 size="s" />
            </Button>
          </div>
        </div>

        {showConnectServices && (
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            <div className={SECTION_HEADER_CLASS}>Services</div>
            <ConnectServicesClientSelector
              clientOptions={clientOptions}
              selectedClient={connectServicesClient}
              onClientChange={setConnectServicesClient}
              showToggle
              showSendTodayText={daysUntilClose !== null && daysUntilClose >= 0 && daysUntilClose <= 20}
              showSendFutureText={daysUntilClose >= 21}
              disableReason={connectServicesDisableReason}
              onDisableReasonChange={setConnectServicesDisableReason}
              onValidityChange={setIsConnectServicesValid}
              clientRoles={clientRoles}
              useSearchClient={useAccountPeopleSearchClient}
              isCloseDateInPast={isCloseDateInPast}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px] tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-t-[1px] tw-border-solid tw-border-gray-10 tw-pt-[16px]">
        <Button onClick={onHide} schema="tertiary" size="medium">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          schema="primary"
          size="medium"
          isLoading={isSubmitting}
          disabled={!isFormValid}
        >
          Create Transaction
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddBuyerTenantTransactionModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  sources: PropTypes.arrayOf(optionShape),
  propertyTypes: PropTypes.arrayOf(optionShape),
  transactionType: PropTypes.string,
  personId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  personName: PropTypes.string,
  defaultPropertyType: optionShape,
};

AddBuyerTenantTransactionModal.defaultProps = {
  show: false,
  onHide: () => {},
  sources: [],
  propertyTypes: [],
  transactionType: "",
  personId: null,
  personName: null,
  defaultPropertyType: null,
};

export default AddBuyerTenantTransactionModal;
